<template>
  <v-container class="pa-0">
    <OverflowTabs
      v-if="isSuperClustersFeatureUnlocked"
      :ordered-menu-items="filterOrder"
      :menu-items-count="menuItemsCount"
      :default-selected-menu-item="selectedDataType"
      @selected-menu="handleSelectedMenu"
    ></OverflowTabs>
    <div class="grid-options-layout" v-if="!isSuperClustersFeatureUnlocked">
      <!--      <v-select-->
      <!--          v-if="showBreakingEvents"-->
      <!--          v-model="selectedDataType"-->
      <!--          :items="filterOrder"-->
      <!--      >-->
      <!--      </v-select>-->
      <OverflowMenu
        v-if="showBreakingEvents"
        :ordered-menu-items="filterOrder"
        :menu-items-count="menuItemsCount"
        :default-selected-menu-item="selectedDataType"
        @selected-menu="handleSelectedMenu"
      ></OverflowMenu>

      <v-tabs
        v-if="!showBreakingEvents"
        v-model="selectedDataTypeIndex"
        :show-arrows="showArrows"
        centered
        grow
        ref="tabs"
      >
        <v-tab
          class="px-3 mx-0 type-tabs"
          v-for="filter in filterOrder"
          :key="filter"
        >
          {{ filter }}
        </v-tab>
      </v-tabs>
    </div>

    <div
      v-if="selectedDataType === filterName.discoverAi"
      class="pa-0 pl-3 mt-4"
    >
      <v-select
        v-model="discoverAiFilter"
        :items="discoverAiFilterItems"
        label="Filter by tags"
        multiple
        chips
        small-chips
        deletable-chips
        clearable
        dense
        hide-details
        @change="handleChangeDiscoverAiFilter"
      ></v-select>
    </div>

    <div v-if="selectedDataType === filterName.hotspots">
      <LatestHotspots
        :params="params"
        @open-hotspot-details="
          $emit(EMITTED_ACTIONS.OPEN_HOTSPOT_DETAILS, $event)
        "
      ></LatestHotspots>
    </div>
    <div v-if="!(selectedDataType === filterName.hotspots)">
      <Loader v-if="loading" text="" color="black" />
      <DetailsListCluster
        v-if="items.length > 0"
        theme="light"
        :items="items"
        :page="page"
        :pageSize="pageSize"
        :height-percent="100"
        :height-pixels="0"
        :show-primary-topics="true"
        :show-thumbs-down="true"
        :show-collaboration="showCollaboration"
        :get-threshold-type-function="getThresholdTypeFunction"
        :scroll-to-top="scrollToTop"
        :matomo-category="analyticsCategory"
        :action-icons-enabled="true"
        @get-data="getContent"
        @loaded="loading = false"
      />
      <h4 class="text-center" v-if="!loading && items.length === 0">
        No headlines available
      </h4>
    </div>
  </v-container>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import { get, isEqual, debounce, cloneDeep } from "lodash";
import { getArticles } from "@/services/search/wod.service";
import { getBreakingEvents } from "@/services/breakingEvents/breakingEvents.service";
import { getSuperClusterHeadlines } from "@/services/superClusters/superClusters.service";
import { setInfiniteScrollState } from "@/common/basic.utilities";
import { getUnlockedPrimaryTopics } from "@/services/events/topics.service";
import { getRiskCategories } from "@/services/controlRisks/risks.service";
import { mapGetters } from "vuex";
import { MODULE_PREFIX_USER } from "@/store";
import { SAVE_PREFERENCES } from "@/store/actions.type";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";
import { SOCIAL_SOURCES } from "@/common/constants/wodSocialSources";
import { TRAVEL_RISK_RATING_ID } from "@/common/constants/controlRisks/risks";

import {
  hasTwitterAccess,
  hasEssentialsAccess,
  hasInternationalSOSAccess,
  hasMaritimeAccess,
} from "@/services/user/user.service";

const FILTER_NAMES = {
  breakingEvents: "Breaking Events",
  eventsAi: "Events AI",
  analysis: "Analysis",
  verifiedEvents: "Verified Events",
  hotspots: "Hotspots",
  travelISOS: "Travel",
  discoverAi: "Discover AI",
};

const DISCOVER_AI_FILTERS = {
  new: { value: "new", text: "New" },
  accelerating: { value: "accelerating", text: "Accelerating Story" },
};

const EMITTED_ACTIONS = {
  OPEN_HOTSPOT_DETAILS: "open-hotspot-details",
};

export default {
  name: "Headlines",
  components: {
    DetailsListCluster: () =>
      import("@/components/common/details/DetailsListCluster"),
    LatestHotspots: () => import("@/components/latest/LatestHotspots"),
    Loader: () => import("@/components/common/Loader"),
    OverflowMenu: () => import("@/components/common/OverflowMenu"),
    OverflowTabs: () => import("@/components/common/OverflowTabs"),
  },
  props: {
    params: {
      type: Object,
    },
    analyticsCategory: {
      type: String,
      default: EVENT_CATEGORIES.headlines,
    },
  },
  data() {
    let _hasEssentials = hasEssentialsAccess();
    const _showBreakingEvents = this.shouldShowBreakingEvents();

    const filterOrder = _hasEssentials
      ? [
          FILTER_NAMES.verifiedEvents,
          FILTER_NAMES.hotspots,
          FILTER_NAMES.analysis,
        ]
      : _showBreakingEvents
      ? [
          FILTER_NAMES.breakingEvents,
          FILTER_NAMES.verifiedEvents,
          FILTER_NAMES.eventsAi,
          FILTER_NAMES.hotspots,
          FILTER_NAMES.analysis,
        ]
      : [
          FILTER_NAMES.verifiedEvents,
          FILTER_NAMES.eventsAi,
          FILTER_NAMES.hotspots,
          FILTER_NAMES.analysis,
        ];

    const filterListOrder = _hasEssentials
      ? [
          FILTER_NAMES.analysis,
          FILTER_NAMES.verifiedEvents,
          FILTER_NAMES.hotspots,
        ]
      : _showBreakingEvents
      ? [
          FILTER_NAMES.discoverAi,
          FILTER_NAMES.breakingEvents,
          FILTER_NAMES.verifiedEvents,
          FILTER_NAMES.analysis,
          FILTER_NAMES.hotspots,
        ]
      : [
          FILTER_NAMES.discoverAi,
          FILTER_NAMES.verifiedEvents,
          FILTER_NAMES.analysis,
          FILTER_NAMES.hotspots,
        ];

    //Only users with ISOS access see the travel tab
    if (
      hasInternationalSOSAccess() &&
      this.featureUnlocked("risk-category-travel")
    ) {
      filterOrder.push(FILTER_NAMES.travelISOS);
      filterListOrder.push(FILTER_NAMES.travelISOS);
    }

    let defaultFilter = _hasEssentials
      ? FILTER_NAMES.analysis
      : this.featureUnlocked("super-clusters")
      ? FILTER_NAMES.discoverAi
      : FILTER_NAMES.eventsAi;

    return {
      EMITTED_ACTIONS: EMITTED_ACTIONS,
      loading: false,
      selectedDataType: defaultFilter,
      selectedDataTypeIndex: filterOrder.findIndex((f) => f == defaultFilter),
      allRiskCategories: "",
      filterOrder: this.featureUnlocked("super-clusters")
        ? filterListOrder
        : filterOrder, // Cleanup filterOrder once feature lock check is removed
      filterName: FILTER_NAMES,
      items: [],
      page: 1,
      pageSize: 25,
      scrollToTop: new Date().toISOString(),
      lastParams: null,
      hasEssentials: _hasEssentials,
      showBreakingEvents: _showBreakingEvents,
      showArrows: false,
      discoverAiFilter: [],
      discoverAiFilterItems: [
        DISCOVER_AI_FILTERS.accelerating,
        DISCOVER_AI_FILTERS.new,
      ],
      menuItemsCount:
        this.featureUnlocked("super-clusters") &&
        _hasEssentials &&
        hasInternationalSOSAccess()
          ? 2
          : 3,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    showCollaboration() {
      return this.selectedDataType != FILTER_NAMES.breakingEvents;
    },
    isSuperClustersFeatureUnlocked() {
      return this.featureUnlocked("super-clusters");
    },
  },
  mounted() {
    this.applyPrefs();
    this.initialize();
    this.updateShowArrows();
  },
  methods: {
    async initialize() {
      this.loading = true;

      this.getContent();
    },
    getContent(page, $state, params) {
      if (!page) {
        this.scrollToTop = new Date().toISOString();
      }
      this.loading = true;
      this.getContentDebounced(page, $state, params);
    },
    getContentDebounced: debounce(async function (page, $state, params) {
      let result;
      let dataParams;
      try {
        this.loading = true;

        dataParams = (await this.getParams()) || {};

        page = !page ? 1 : page;
        const PAGE_OFFSET = (page - 1) * this.pageSize;

        dataParams.pageSize = this.pageSize;
        dataParams.pageOffset = PAGE_OFFSET;

        let currParams = cloneDeep(dataParams);
        if (currParams.hasOwnProperty("pageOffset")) {
          delete currParams.pageOffset;
        }

        let prevParams = cloneDeep(this.lastParams);
        if (prevParams && prevParams.hasOwnProperty("pageOffset")) {
          delete prevParams.pageOffset;
        }
        if (!isEqual(currParams, prevParams)) {
          this.lastTimestamp = new Date().toISOString();
        }

        this.lastParams = dataParams;

        if (page > 1) {
          this.handlePageTrackerEvent(
            this.analyticsCategory,
            EVENT_ACTIONS.scrolled,
            `Page ${page}`
          );
        }

        if (this.selectedDataType === FILTER_NAMES.breakingEvents) {
          // Business req calls that we query last 7 days AAAS-7275
          dataParams.start = dayjs().subtract(7, "day").toISOString();
          result = await getBreakingEvents(dataParams, this.lastTimestamp);
        } else if (this.selectedDataType === FILTER_NAMES.discoverAi) {
          result = await getSuperClusterHeadlines(
            dataParams,
            this.lastTimestamp
          );
        } else {
          result = await getArticles(dataParams, this.lastTimestamp);
        }
        if (result && result.features) {
          if (
            this.itemsResults &&
            Array.isArray(this.itemsResults) &&
            this.itemsResults.length &&
            page !== 1
          ) {
            this.itemsResults = this.itemsResults.concat(result.features);
          } else {
            this.itemsResults = result.features;
          }
          this.items = cloneDeep(this.itemsResults);
        } else {
          this.itemsResults = null;
          this.featuresResults = null;
        }
      } catch (error) {
        console.error("Error in getContent: " + JSON.stringify(error));
        console.error(error);
      } finally {
        if (result) {
          setInfiniteScrollState(
            $state,
            { features: result.features },
            "features"
          );
        }
        this.page = page;
        this.loading = false;
        result = null;
      }
    }, 100),
    async getParams() {
      let paramsCopy = cloneDeep(this.params); // Cubler - What Fields are important for a query?
      const dataAI = this.isSuperClustersFeatureUnlocked
        ? FILTER_NAMES.discoverAi
        : FILTER_NAMES.eventsAi;
      paramsCopy.pageSize = this.pageSize;
      paramsCopy.pageOffset = this.page * this.pageOffset;
      paramsCopy.sortDirection = "desc";
      paramsCopy.sources = this.getSource();
      paramsCopy.eventType = "all";

      //Remove bounds since the Latest page only filters by aoiId
      delete paramsCopy.bounds;
      delete paramsCopy.boundsType;

      // Set additional parameters based on selected data type
      switch (this.selectedDataType) {
        case FILTER_NAMES.analysis:
          if (!this.allRiskCategories) {
            this.allRiskCategories = (
              await getRiskCategories({
                ui: true,
              })
            )
              .map((cat) => cat.id)
              .filter((r) => r !== TRAVEL_RISK_RATING_ID)
              .join(",");
          }
          paramsCopy.riskCategories = this.allRiskCategories;
          break;
        case FILTER_NAMES.breakingEvents:
          break;
        case dataAI:
          paramsCopy.topics = getUnlockedPrimaryTopics()
            .map((t) => t.apiName)
            .toString();

          if (dataAI === FILTER_NAMES.discoverAi) {
            if (this.discoverAiFilter.includes(DISCOVER_AI_FILTERS.new.value)) {
              paramsCopy.isNew = true;
            }
            if (
              this.discoverAiFilter.includes(
                DISCOVER_AI_FILTERS.accelerating.value
              )
            ) {
              paramsCopy.isAccelerating = true;
            }
          }
          break;
        case FILTER_NAMES.hotspots:
          paramsCopy.hotspotsOnly = true;
          break;
        case FILTER_NAMES.travelISOS:
          if (hasInternationalSOSAccess) {
            paramsCopy.riskCategories = TRAVEL_RISK_RATING_ID;
          }
          break;
        case FILTER_NAMES.verifiedEvents:
          // No additional fields needed.
          break;
      }

      return paramsCopy;
    },
    // copied from SearchContent
    getThresholdTypeFunction(doc) {
      let thresholdType = "cluster";
      if (
        get(doc, "properties.formatted.source") === "twitter.com" ||
        get(doc, "properties.formatted.source") === "twitter"
      ) {
        thresholdType = "twitter";
      }
      return thresholdType;
    },
    resetState() {
      this.lastTimestamp = null;
      this.page = 0;
      this.items = [];
    },
    getSource() {
      let sources = [];
      switch (this.selectedDataType) {
        case FILTER_NAMES.analysis:
          sources.push("analysis");
          break;
        case FILTER_NAMES.eventsAi:
          sources.push("news");
          if (hasTwitterAccess()) {
            sources.push(SOCIAL_SOURCES);
          }
          break;
        case FILTER_NAMES.hotspots:
          sources.push("news");
          if (hasTwitterAccess()) {
            sources.push(SOCIAL_SOURCES);
          }
          break;
        case FILTER_NAMES.travelISOS:
          if (hasInternationalSOSAccess) {
            sources.push("analysis");
          }
          break;
        case FILTER_NAMES.verifiedEvents:
          sources.push("political");
          if (hasMaritimeAccess()) {
            sources.push("maritime");
          }
          break;
      }

      return sources.join(",");
    },
    applyPrefs() {
      const latestPrefs = this.preferences?.latest || {};
      if (Object.hasOwn(latestPrefs, "selectedDataType")) {
        this.selectedDataType = latestPrefs.selectedDataType;
      }
      if (
        Object.hasOwn(latestPrefs, "discoverAi") &&
        Object.hasOwn(latestPrefs.discoverAi, "filter")
      ) {
        this.discoverAiFilter = latestPrefs.discoverAi.filter;
      }
    },
    setPrefs() {
      //Start with default values
      if (!this.preferences) {
        this.preferences = {};
      }
      if (!this.preferences.latest) {
        this.preferences.latest = {};
      }

      //Set the headlines prefs on the parent preferences object
      Object.assign(this.preferences.latest, {
        selectedDataType: this.selectedDataType,
      });

      this.preferences.latest.discoverAi =
        this.preferences.latest.discoverAi || {};
      this.preferences.latest.discoverAi.filter = [];
      if (this.discoverAiFilter.includes(DISCOVER_AI_FILTERS.new.value)) {
        this.preferences.latest.discoverAi.filter.push(
          DISCOVER_AI_FILTERS.new.value
        );
      }
      if (
        this.discoverAiFilter.includes(DISCOVER_AI_FILTERS.accelerating.value)
      ) {
        this.preferences.latest.discoverAi.filter.push(
          DISCOVER_AI_FILTERS.accelerating.value
        );
      }

      //Dispatch the changes
      this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        this.preferences
      );
    },
    shouldShowBreakingEvents() {
      return this.featureUnlocked("breaking-events") && !hasEssentialsAccess();
    },
    updateShowArrows() {
      let tabsContainer = this.$refs?.tabs?.$el; //.clientWidth
      let tabsElement = tabsContainer
        ? tabsContainer.querySelector(".v-slide-group__content")
        : null;
      if (tabsContainer && tabsElement) {
        let containerWidth = tabsContainer.clientWidth;
        let tabsWidth = tabsElement.clientWidth;
        this.showArrows = containerWidth < tabsWidth;
      }
    },
    handleSelectedMenu(params) {
      this.selectedDataType = params.item;
      this.selectedDataTypeIndex = params.index;
    },
    handleChangeDiscoverAiFilter() {
      this.setPrefs();
      this.resetState();
      this.getContent();
    },
  },
  watch: {
    params: {
      async handler() {
        this.resetState();
        this.getContent();
      },
      deep: true,
    },
    selectedDataType: {
      async handler() {
        this.setPrefs();
        this.resetState();
        this.getContent();
        this.handlePageTrackerEvent(
          this.analyticsCategory,
          EVENT_ACTIONS.changed,
          `Content Type - ${this.selectedDataType} - "on"`
        );
      },
      deep: true,
    },
    selectedDataTypeIndex: {
      async handler() {
        if (!this.showBreakingEvents) {
          this.selectedDataType = this.filterOrder[this.selectedDataTypeIndex];
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
/* Content type checkboxes */
>>> .v-input--selection-controls {
  margin-top: 0;
}
>>> .v-input__slot .v-input--selection-controls__input {
  margin-bottom: auto;
}
.grid-options-layout-entry >>> .v-label {
  font-size: 13px;
}
>>> .v-input--selection-controls.v-input--dense
  .v-input--selection-controls__ripple {
  width: 18px;
  height: 18px;
  top: calc(50% - 16px);
}
>>> .v-input__slot .v-input--selection-controls__input {
  margin-bottom: auto;
  height: 15px;
  width: 15px;
  top: 2px;
}
>>> .v-input--selection-controls .v-icon.v-icon--dense {
  font-size: 15px;
}

.grid-options-layout {
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: left;
}

@media only screen and (max-width: 1200px) {
  .ve {
    margin-top: 10px;
    padding-left: 10px !important;
  }
  .grid-options-layout {
    flex-wrap: wrap;
  }
}

.type-tabs {
  font-size: smaller;
}
</style>
